import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
// Direct-module import, not the package barrel — see index.ts's re-export comment for why.
import { useAuthStore } from '../../../../passport/packages/@passport/store/src/authStore'
import { useAuthzStore } from '../../../../passport/packages/@passport/store/src/authzStore'
import {
  UserIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  SettingsIcon,
  WrenchIcon,
  PackageBoxIcon,
  LogoutIcon,
  ExternalLinkIcon,
  TenantName,
} from '@bizfirst/common-react'
import type { DevToolLink, AppToolLink } from './AuthHeaderControls'
import { buildLogoutAppUrl } from './LoginRedirectPage'

export interface UserMenuButtonProps {
  /** Route or absolute URL for "View User Profile". See AuthHeaderControlsProps.profileUrl. */
  profileUrl?: string
  /** Route or absolute URL for "View App Settings". See AuthHeaderControlsProps.appSettingsUrl. */
  appSettingsUrl?: string
  /** Entries for the "Developer Tools" row. Omit to hide the row entirely. */
  devTools?: DevToolLink[]
  /**
   * Role ID the current user must have (checked via `useAuthzStore().hasRole()`) for
   * the "Developer Tools" row to render. Defaults to 'IsDeveloper'.
   *
   * IMPORTANT: `hasRole(roleId)` matches against `Role.id` in `@passport/store`'s
   * `useAuthzStore`, NOT `Role.name` (verified directly against
   * `authzStore.ts`: `state.roles.some((r) => r.id === roleId)`). Whoever configures
   * roles on the backend must issue a role whose `id` field is literally the string
   * passed here (e.g. `"IsDeveloper"`) — a role with a matching display *name* but a
   * different `id` will NOT satisfy this gate. Pass an empty string explicitly (not just
   * omitting the prop, which keeps the 'IsDeveloper' default below) to show the row to
   * every authenticated user regardless of role — e.g. app-template does this since it's
   * a starter template, not a role-gated production app.
   */
  requiredDevToolsRole?: string
  /** Entries for the "Apps Tools" row. Omit to hide the row entirely. Not role-gated. */
  appTools?: AppToolLink[]
  /**
   * Called after this app's own local session is cleared, before the guaranteed
   * redirect to the login app's `/logout` route fires below — e.g. app-specific extra
   * cleanup. No longer responsible for the redirect itself (see `handleLogout`).
   */
  onAfterLogout?: () => void
  /**
   * Which cross-app handoff mechanism this app expects back on its NEXT sign-in after
   * this logout — mirrors `LoginRedirectPageProps.handoffMode`. Pass `'code'` here if
   * this app also passes `handoffMode="code"` to its own `LoginRedirectPage`; otherwise
   * the logout redirect below carries no handoff preference and the login app falls
   * back to the legacy `?token=<raw JWT>` handoff on the next sign-in, silently
   * reintroducing the exact URL-token exposure `'code'` exists to avoid. Omit (or
   * `'token'`) for every app that hasn't migrated its sign-in flow.
   */
  handoffMode?: 'token' | 'code'
}

type SubmenuKey = 'appTools' | 'devTools' | null

// The panel is portaled to <body> and uses the design-system dropdown layer (--z-index-dropdown: 500), so it
// stays below modal overlays (--z-index-modal-overlay: 1000). Apps whose sticky chrome sits above 500 raise
// the panel in their own stylesheet, scoped to that app (see Flow Studio's studio-theme.css).
const PANEL_Z_INDEX = 500

function initials(name: string | undefined, email: string | undefined): string {
  const source = name?.trim() || email?.trim() || ''
  if (!source) return ''
  return source.charAt(0).toUpperCase()
}

/**
 * `UserMenuButton` — avatar + name, rendered only while logged in (design doc §4.2).
 * `AuthHeaderControls` is the one that decides between this and `SignInButton` — this
 * component has no internal branching on auth state beyond reading the user object,
 * so it assumes a logged-in user is already present.
 *
 * NOTE ON `useAuthzStore` INITIALIZATION: nothing in this component calls
 * `initAuthzStore`/`loadPermissions`/`refreshPermissionsIfStale`. Verified before
 * writing this: no real Passport app (admin-dashboard, user-portal, sso-management,
 * security-dashboard, login) calls those either — admin-dashboard's own App.tsx gates
 * its admin routes on `user.roles.includes('admin')` from `useAuthStore` directly, not
 * on `useAuthzStore` at all, and `initAuthzStore` has zero call sites anywhere in
 * `src/passport`. Since there's no established real-app convention to match, this
 * follows the simplest reasonable choice explicitly sanctioned by the design doc:
 * read `hasRole()` directly. `useAuthzStore` is documented as fail-secure (denies
 * while `loadingState` is `'loading'`/`'error'`) specifically so an uninitialized
 * store correctly hides the Developer Tools row rather than showing it — the same
 * behavior a properly-initialized-but-still-loading store would produce.
 */
export function UserMenuButton({
  profileUrl,
  appSettingsUrl,
  devTools,
  requiredDevToolsRole = 'IsDeveloper',
  appTools,
  onAfterLogout,
  handoffMode,
}: UserMenuButtonProps): React.ReactElement | null {
  const { user, logout } = useAuthStore()
  const { hasRole } = useAuthzStore()

  const [isOpen, setIsOpen] = useState(false)
  const [openSubmenu, setOpenSubmenu] = useState<SubmenuKey>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (buttonRef.current && panelRef.current) {
        if (!buttonRef.current.contains(event.target as Node) && !panelRef.current.contains(event.target as Node)) {
          setIsOpen(false)
          setOpenSubmenu(null)
        }
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside as EventListener)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('touchstart', handleClickOutside as EventListener)
      }
    }
    return undefined
  }, [isOpen])


  if (!user) {
    return null
  }

  const closeAll = () => {
    setIsOpen(false)
    setOpenSubmenu(null)
  }

  // Clears this app's own local session, then unconditionally sends the browser to the
  // login app's /logout route — that route (LogoutPage.tsx) is what actually revokes
  // the session server-side and clears the login app's OWN separate localStorage.
  // Without this redirect, only this app's local copy was ever cleared: the login
  // app's own session (a different origin, its own localStorage) silently survived,
  // so visiting any other app would re-authenticate the user without ever showing a
  // login form. Previously this redirect was opt-in per app via `onAfterLogout` — ~20
  // apps remembered to wire `onAfterLogout={() => window.location.href =
  // buildLogoutAppUrl(window.location.origin)}` themselves, any app that didn't had a
  // broken logout. Centralizing it here fixes it for every consuming app at once.
  const handleLogout = async () => {
    closeAll()
    await logout()
    onAfterLogout?.()
    window.location.href = buildLogoutAppUrl(window.location.origin, handoffMode)
  }

  const showDevTools = Boolean(devTools && devTools.length > 0 && (!requiredDevToolsRole || hasRole(requiredDevToolsRole)))
  const showAppTools = Boolean(appTools && appTools.length > 0)

  // Calculate dropdown position relative to button when using Portal
  const getPanelPositionStyles = (): React.CSSProperties => {
    if (!buttonRef.current) {
      return { position: 'fixed', top: 0, right: 8 }
    }

    const rect = buttonRef.current.getBoundingClientRect()
    const isSmallScreen = window.innerWidth <= 768  // Include tablets (not just mobile <= 640px)
    const dropdownHeight = 400  // Approximate dropdown height
    const spaceBelow = window.innerHeight - rect.bottom

    if (isSmallScreen) {
      // On mobile/tablet: position panel to stay within viewport
      const panelWidth = 260  // Design spec: mobile panel width
      const viewportMargin = 8  // Minimum margin from viewport edge

      // Position panel so right edge stays within viewport with margin
      // Formula: rightPos = window.innerWidth - panelWidth - margin
      const rightPos = Math.max(viewportMargin, window.innerWidth - panelWidth - viewportMargin)

      // Check if dropdown fits below; if not, position above
      const topPos = spaceBelow > dropdownHeight
        ? rect.bottom + 8
        : Math.max(8, rect.top - dropdownHeight - 8)

      return {
        position: 'fixed' as const,
        top: topPos,
        right: rightPos,
        left: 'auto',
        zIndex: PANEL_Z_INDEX,
        maxHeight: 'calc(100vh - 16px)',
        maxWidth: `${panelWidth}px`,
      }
    }

    // On desktop: align the panel's right edge with the button's right edge. Position from
    // `left` (measured from the viewport's left edge), not `right`: `right` is measured from the
    // containing block's right edge, which can sit inside the window by a scrollbar width, so the
    // panel drifted left of the trigger.
    const desktopPanelWidth = 260  // Matches .auth-user-menu-panel width
    return {
      position: 'fixed' as const,
      top: rect.bottom + 8,
      left: rect.right - desktopPanelWidth,
      zIndex: PANEL_Z_INDEX,
    }
  }

  const renderGroupRow = (
    key: 'appTools' | 'devTools',
    label: string,
    icon: React.ReactNode,
    entries: (DevToolLink | AppToolLink)[]
  ) => {
    if (entries.length === 1) {
      const entry = entries[0]
      if (!entry) {
        return null
      }
      return (
        <MenuLinkItem
          key={key}
          icon={icon}
          label={label}
          href={entry.href}
          onClick={entry.onClick}
          onNavigate={closeAll}
        />
      )
    }

    const isSubmenuOpen = openSubmenu === key
    return (
      <div key={key} className="auth-user-menu-row-wrapper">
        <button
          type="button"
          className="auth-user-menu-row auth-user-menu-row-parent"
          onClick={() => setOpenSubmenu(isSubmenuOpen ? null : key)}
          aria-expanded={isSubmenuOpen}
        >
          <span className="auth-user-menu-row-icon">{icon}</span>
          <span className="auth-user-menu-row-label">{label}</span>
          <ChevronRightIcon size={14} className="auth-user-menu-row-chevron" />
        </button>
        {isSubmenuOpen && (
          <div className="auth-user-menu-submenu">
            {entries.map((entry, index) => (
              <MenuLinkItem
                key={`${key}-${index}`}
                label={entry.label}
                icon={entry.icon}
                href={entry.href}
                onClick={entry.onClick}
                onNavigate={closeAll}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="auth-user-menu">
      <button
        ref={buttonRef}
        className="auth-user-menu-button"
        onClick={() => setIsOpen((open) => !open)}
        title={user.name || user.email}
        aria-label="Open user menu"
        aria-expanded={isOpen}
      >
        <span className="auth-user-menu-avatar">{initials(user.name, user.email) || <UserIcon size={16} />}</span>
        <span className="auth-user-menu-name">{user.name || user.email}</span>
        <ChevronDownIcon size={16} className="auth-user-menu-chevron" />
      </button>

      {isOpen && createPortal(
        <div ref={panelRef} className="auth-user-menu-panel" style={getPanelPositionStyles()}>
          <div className="auth-user-menu-identity">
            <span className="auth-user-menu-avatar auth-user-menu-avatar-lg">
              {initials(user.name, user.email) || <UserIcon size={20} />}
            </span>
            <div className="auth-user-menu-identity-text">
              <div className="auth-user-menu-identity-name">{user.name || 'User'}</div>
              {user.email && <div className="auth-user-menu-identity-email">{user.email}</div>}
              <TenantName as="div" className="auth-user-menu-identity-tenant" />
            </div>
          </div>

          <div className="auth-user-menu-divider" />

          <MenuLinkItem icon={<UserIcon size={16} />} label="View User Profile" href={profileUrl} onNavigate={closeAll} />
          <MenuLinkItem icon={<SettingsIcon size={16} />} label="View App Settings" href={appSettingsUrl} onNavigate={closeAll} />

          {showAppTools && renderGroupRow('appTools', 'Apps Tools', <PackageBoxIcon size={16} />, appTools!)}
          {showDevTools && renderGroupRow('devTools', 'Developer Tools', <WrenchIcon size={16} />, devTools!)}

          <div className="auth-user-menu-divider" />

          <button type="button" className="auth-user-menu-row auth-user-menu-row-danger" onClick={handleLogout}>
            <span className="auth-user-menu-row-icon">
              <LogoutIcon size={16} />
            </span>
            <span className="auth-user-menu-row-label">Logout</span>
          </button>
        </div>,
        document.body
      )}

      <style>{`
        .auth-user-menu {
          position: relative;
          display: flex;
          align-items: center;
          z-index: 1;
        }

        .auth-user-menu-button {
          display: flex;
          align-items: center;
          gap: 8px;
          height: 40px;
          padding: 0 10px 0 6px;
          background: var(--bg-raised);
          border: 1px solid var(--border-default); box-sizing: border-box;
          border-radius: 6px;
          color: inherit;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .auth-user-menu-button:hover {
          background: var(--border-default);
        }

        .auth-user-menu-avatar {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 28px;
          height: 28px;
          border-radius: 999px;
          background: var(--color-primary, var(--obs-primary, #309B48));
          color: var(--text-on-primary);
          font-size: 13px;
          font-weight: 700;
        }

        .auth-user-menu-avatar-lg {
          width: 36px;
          height: 36px;
          font-size: 15px;
        }

        .auth-user-menu-name {
          max-width: 140px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 14px;
          font-weight: 500;
        }

        /* Mobile responsive button text */
        @media (max-width: 640px) {
          .auth-user-menu-name {
            max-width: 60px;
            font-size: 13px;
          }
        }

        .auth-user-menu-chevron {
          flex-shrink: 0;
          opacity: 0.7;
        }

        /* Narrow phones: the header row is only ~360px of usable width once the hamburger,
           logo, title, bell and app-links button have taken their share, and the name span
           alone was up to 140px of that -- enough to push this button and the app-links
           button off the edge of the header, where overflow: hidden made them untappable.
           Collapse to the avatar only; the full name is still shown inside the open menu. */
        @media (max-width: 480px) {
          .auth-user-menu-name,
          .auth-user-menu-chevron {
            display: none;
          }
        }

        .auth-user-menu-panel {
          width: 260px;
          background: var(--color-bg-secondary, var(--bg-secondary, #1c1f26));
          border: 1px solid var(--color-border, var(--border, #30363d));
          border-radius: 8px;
          box-shadow: 0 10px 25px rgb(var(--black-rgb) / 0.4);
          padding: 8px 0;
          animation: authUserMenuSlideDown 0.2s ease;
          max-height: 90vh;
          overflow-y: auto;
        }

        /* Mobile/tablet responsive dropdown width (≤768px includes tablets) */
        @media (max-width: 768px) {
          .auth-user-menu-panel {
            width: auto;
            min-width: 260px;
            max-width: calc(100vw - 16px);
          }
        }

        @keyframes authUserMenuSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .auth-user-menu-identity {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 16px 12px;
        }

        .auth-user-menu-identity-text {
          min-width: 0;
        }

        .auth-user-menu-identity-name {
          font-size: 14px;
          font-weight: 600;
          color: var(--color-text-primary, var(--text-primary, #e6edf3));
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .auth-user-menu-identity-email {
          font-size: 12px;
          color: var(--color-text-secondary, var(--text-secondary, #6B7879));
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .auth-user-menu-identity-tenant {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.02em;
          color: var(--color-text-tertiary, var(--text-tertiary, #8b949e));
          margin-top: 4px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .auth-user-menu-divider {
          height: 1px;
          margin: 4px 0;
          background: var(--color-border, var(--border, #30363d));
        }

        .auth-user-menu-row-wrapper {
          position: relative;
        }

        .auth-user-menu-row {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 10px 16px;
          min-height: 40px;
          background: none;
          border: none;
          color: var(--color-text-secondary, var(--text-secondary, #6B7879));
          text-decoration: none;
          font-size: 14px;
          text-align: left;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        /* Mobile: WCAG AA 44px touch target */
        @media (max-width: 768px) {
          .auth-user-menu-row {
            min-height: 44px;
            padding: 12px 16px;
          }
        }

        .auth-user-menu-row:hover {
          background: var(--color-bg-tertiary, var(--bg-tertiary, #30363d));
          color: var(--color-primary, var(--obs-primary, #309B48));
        }

        .auth-user-menu-row:active {
          background: var(--color-bg-tertiary, var(--bg-tertiary, #30363d));
          opacity: 0.8;
        }

        .auth-user-menu-row-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 16px;
          height: 16px;
        }

        .auth-user-menu-row-label {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .auth-user-menu-row-chevron {
          flex-shrink: 0;
          opacity: 0.6;
        }

        .auth-user-menu-row-external {
          flex-shrink: 0;
          opacity: 0.6;
        }

        .auth-user-menu-row-danger {
          color: var(--error);
        }

        .auth-user-menu-row-danger:hover {
          background: rgb(var(--error-rgb) / 0.1);
          color: var(--error);
        }

        /* Inline accordion, not a sideways flyout: this row's own box (.auth-user-menu-row-wrapper)
           spans the full panel width, but its visible icon+label sit well inside that box (10px
           16px padding) -- a flyout anchored to the box's edge via a right-offset of 100% plus a
           few px then lands well to the left of the visible label, reading as disconnected from
           the row that opened it even though its position is technically correct relative to
           that box. Expanding inline below the row (the same treatment this used to apply only
           under 480px, now applied at every width) keeps the submenu visually attached to what
           the user actually clicked, and removes the positioning math entirely rather than
           re-deriving a new offset. */
        .auth-user-menu-submenu {
          position: static;
          min-width: 0;
          margin: 2px 8px 6px 24px;
          background: var(--color-bg-secondary, var(--bg-secondary, #1c1f26));
          border: 1px solid var(--color-border, var(--border, #30363d));
          border-radius: 6px;
          box-shadow: none;
          padding: 8px 0;
          z-index: 10000;
          max-height: 80vh;
          overflow-y: auto;
        }

        /* Mobile/tablet responsive submenu (≤768px includes tablets) */
        @media (max-width: 768px) {
          .auth-user-menu-submenu {
            position: absolute;
            top: 0;
            right: calc(100% - 10px);
            left: auto;
            min-width: 180px;
          }
        }

        /* Phones: the flyout opens to the LEFT of a 260px panel that is itself near the right edge,
           so a 200px submenu landed at x=-139 on a 382px screen -- entirely off-screen and
           unreachable. There is no horizontal room for a flyout at this width, so drop out of
           absolute positioning and let it flow inline under its own row (it is already the next
           sibling inside a block .auth-user-menu-row-wrapper, and the panel has no max-height or
           overflow clipping), which is the usual mobile accordion pattern. */
        @media (max-width: 480px) {
          .auth-user-menu-submenu {
            position: static;
            min-width: 0;
            margin: 2px 8px 6px 24px;
            box-shadow: none;
            border-radius: 6px;
          }
        }
      `}</style>
    </div>
  )
}

interface MenuLinkItemProps {
  label: string
  icon?: React.ReactNode
  href?: string
  onClick?: () => void
  onNavigate: () => void
}

/** A single clickable dropdown row — renders as a link when `href` is given, a button otherwise. */
function MenuLinkItem({ label, icon, href, onClick, onNavigate }: MenuLinkItemProps): React.ReactElement {
  const handleClick = () => {
    onClick?.()
    onNavigate()
  }

  const isExternal = Boolean(href && /^https?:\/\//.test(href))

  if (href) {
    return (
      <a
        href={href}
        className="auth-user-menu-row"
        onClick={handleClick}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
      >
        {icon && <span className="auth-user-menu-row-icon">{icon}</span>}
        <span className="auth-user-menu-row-label">{label}</span>
        {isExternal && <ExternalLinkIcon size={14} className="auth-user-menu-row-external" />}
      </a>
    )
  }

  return (
    <button type="button" className="auth-user-menu-row" onClick={handleClick}>
      {icon && <span className="auth-user-menu-row-icon">{icon}</span>}
      <span className="auth-user-menu-row-label">{label}</span>
    </button>
  )
}

export default UserMenuButton
